
import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d614nabn6d2i4h4oa4i0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactCards = [
    {
      icon: 'ri-phone-line',
      title: 'Telefone',
      value: '(11) 9 6415-7039',
      subtitle: 'Entre em contato via Whatsapp ou ligação',
    },
    {
      icon: 'ri-mail-line',
      title: 'Email',
      value: 'contato@centraldamulta.app.br',
      subtitle: 'Resposta em até 24h',
    },
    {
      icon: 'ri-time-line',
      title: 'Horário',
      value: 'Segunda a Sexta',
      subtitle: '9h às 18h',
    },
  ];

  return (
    <section id="contato" className="py-12 md:py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900"></div>
      
      {/* Animated Orbs */}
      <div className="absolute top-0 left-1/4 w-48 md:w-96 h-48 md:h-96 bg-yellow-500/5 rounded-full blur-3xl animate-blob"></div>
      <div className="absolute bottom-0 right-1/4 w-48 md:w-96 h-48 md:h-96 bg-amber-500/5 rounded-full blur-3xl animate-blob" style={{ animationDelay: '2s' }}></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-8 md:mb-16 scroll-animate">
          <span className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-yellow-500/10 rounded-full mb-4 md:mb-6 border border-yellow-500/20 backdrop-blur-sm">
            <i className="ri-chat-3-line text-yellow-400 text-sm md:text-base"></i>
            <span className="text-xs md:text-sm font-semibold text-yellow-400">Fale Conosco</span>
          </span>
          <h2 className="text-2xl md:text-5xl font-bold text-white mb-2 md:mb-4">Entre em Contato</h2>
          <p className="text-sm md:text-xl text-gray-400 max-w-2xl mx-auto">
            Pronto para transformar seu negócio? Fale com nossa equipe.
          </p>
        </div>

        {/* Cards de Contato */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-8 md:mb-16">
          {contactCards.map((card, index) => (
            <div 
              key={index}
              className={`scroll-animate delay-${index * 100} bg-gray-800/50 backdrop-blur-xl rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-700/50 hover:border-yellow-500/30 transition-all duration-500 cursor-pointer group hover:transform hover:-translate-y-2 hover:shadow-xl hover:shadow-yellow-500/10`}
            >
              <div className="w-10 md:w-14 h-10 md:h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl md:rounded-2xl flex items-center justify-center mb-3 md:mb-5 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-yellow-500/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent"></div>
                <i className={`${card.icon} text-white text-lg md:text-2xl relative z-10`}></i>
              </div>
              <h3 className="font-bold text-white text-sm md:text-lg mb-1 md:mb-2 group-hover:text-yellow-400 transition-colors duration-300">{card.title}</h3>
              <p className="text-gray-300 text-xs md:text-base mb-0.5 md:mb-1">{card.value}</p>
              <p className="text-[10px] md:text-sm text-gray-500">{card.subtitle}</p>
            </div>
          ))}
        </div>

        {/* Formulário */}
        <div className="max-w-4xl mx-auto scroll-animate">
          <div className="relative bg-gray-800/50 backdrop-blur-xl rounded-2xl md:rounded-3xl p-5 md:p-10 border border-gray-700/50 overflow-hidden">
            {/* Shine Effect */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent"></div>
            
            {/* Glow */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-yellow-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl"></div>

            <div className="text-center mb-5 md:mb-8 relative z-10">
              <h3 className="text-lg md:text-2xl font-bold text-white mb-1 md:mb-2">Envie sua mensagem</h3>
              <p className="text-xs md:text-base text-gray-400">Preencha o formulário e entraremos em contato</p>
            </div>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5 mb-3 md:mb-5">
                {[
                  { id: 'name', label: 'Nome Completo', type: 'text', placeholder: 'Seu nome', required: true },
                  { id: 'email', label: 'Email', type: 'email', placeholder: 'seu@email.com', required: true },
                  { id: 'phone', label: 'Telefone', type: 'tel', placeholder: '(00) 00000-0000', required: true },
                  { id: 'company', label: 'Empresa', type: 'text', placeholder: 'Nome da sua empresa', required: false },
                ].map((field) => (
                  <div key={field.id} className="relative">
                    <label htmlFor={field.id} className="block text-xs md:text-sm font-semibold text-gray-300 mb-1 md:mb-2">
                      {field.label} {field.required && '*'}
                    </label>
                    <div className="relative">
                      <input
                        type={field.type}
                        id={field.id}
                        name={field.id}
                        value={formData[field.id as keyof typeof formData]}
                        onChange={handleChange}
                        onFocus={() => setFocusedField(field.id)}
                        onBlur={() => setFocusedField(null)}
                        required={field.required}
                        className={`w-full px-3 md:px-4 py-2.5 md:py-3 bg-gray-900/50 border rounded-lg md:rounded-xl focus:outline-none transition-all duration-300 text-xs md:text-sm text-white placeholder-gray-500 ${
                          focusedField === field.id 
                            ? 'border-yellow-500 shadow-lg shadow-yellow-500/20' 
                            : 'border-gray-700 hover:border-gray-600'
                        }`}
                        placeholder={field.placeholder}
                      />
                      {focusedField === field.id && (
                        <div className="absolute inset-0 rounded-lg md:rounded-xl bg-gradient-to-r from-yellow-500/10 to-amber-500/10 pointer-events-none"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mb-4 md:mb-6 relative">
                <label htmlFor="message" className="block text-xs md:text-sm font-semibold text-gray-300 mb-1 md:mb-2">
                  Mensagem *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  required
                  maxLength={500}
                  rows={3}
                  className={`w-full px-3 md:px-4 py-2.5 md:py-3 bg-gray-900/50 border rounded-lg md:rounded-xl focus:outline-none transition-all duration-300 resize-none text-xs md:text-sm text-white placeholder-gray-500 ${
                    focusedField === 'message' 
                      ? 'border-yellow-500 shadow-lg shadow-yellow-500/20' 
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                  placeholder="Como podemos ajudar você?"
                />
                <p className="text-[10px] md:text-xs text-gray-500 mt-1">Máximo de 500 caracteres</p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-3 md:p-4 bg-green-500/10 border border-green-500/30 rounded-lg md:rounded-xl mb-4 md:mb-6 backdrop-blur-sm">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-6 md:w-8 h-6 md:h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-green-400 text-base md:text-xl"></i>
                    </div>
                    <p className="text-green-400 text-xs md:text-sm font-medium">
                      Mensagem enviada com sucesso! Entraremos em contato em breve.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-3 md:p-4 bg-red-500/10 border border-red-500/30 rounded-lg md:rounded-xl mb-4 md:mb-6 backdrop-blur-sm">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="w-6 md:w-8 h-6 md:h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                      <i className="ri-error-warning-line text-red-400 text-base md:text-xl"></i>
                    </div>
                    <p className="text-red-400 text-xs md:text-sm font-medium">
                      Erro ao enviar mensagem. Por favor, tente novamente.
                    </p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 md:py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-gray-900 rounded-lg md:rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap relative overflow-hidden group hover:shadow-xl hover:shadow-yellow-500/30 hover:scale-[1.02] text-sm md:text-base"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Enviar Mensagem
                    </>
                  )}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-amber-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:left-[100%] transition-all duration-700"></div>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
